"use client";

import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Avatar } from "@calcom/ui/components/avatar";
import { Button } from "@calcom/ui/components/button";
import { TextField } from "@calcom/ui/components/form";
import { Icon } from "@calcom/ui/components/icon";
import { showToast } from "@calcom/ui/components/toast";
import { useRouter } from "next/navigation";
import type { MouseEvent, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  fetchGhostTelegramProfile,
  ghostTelegramAvatarUrl,
  submitGhostBookingRequest,
  type GhostBookingLocale,
  type GhostProfile,
} from "./knopkaGhostApi";
import { buildGhostSchedule, type GhostDay } from "./knopkaGhostSlots";
import {
  closeTelegramMiniApp,
  ghostHaptic,
  ghostTelegramColorScheme,
  ghostTelegramVisitorName,
  isTelegramMiniApp,
  openExternalUrl,
  openTelegramDeepLink,
  prepareTelegramViewport,
  setGhostDocumentTitle,
  subscribeGhostTelegramBackButton,
  subscribeGhostTelegramTheme,
} from "./knopkaGhostTelegram";

/**
 * Safety-net for the two bottom links: a router.push() transition or a
 * mailto: "navigation" (which often never actually leaves the page — there's
 * no browser event that fires when nothing happens) has no reliable
 * completion signal, so a stuck "Opening…" state would otherwise be
 * permanent. Cleared immediately if the component unmounts first (real
 * navigation happened).
 */
const GHOST_LINK_BUSY_RESET_MS = 2500;

type ViewState = "checking" | "unavailable" | "ready";

/**
 * Ghost Booking has its own tiny RU/EN dictionary instead of touching
 * cal.diy's shared i18n resources (plan: "Тексты RU/EN — локальным
 * словарём внутри модуля, общий i18n cal.diy не трогаем").
 */
type GhostDictionary = {
  loading: string;
  timezoneLabel: (tz: string) => string;
  chooseDay: string;
  chooseTime: string;
  yourName: string;
  yourNamePlaceholder: string;
  confirm: string;
  openingTelegram: string;
  preparingMessage: string;
  openingLink: string;
  messageWithoutBooking: string;
  errorSubmit: string;
  disclaimer: string;
  claimLink: string;
  deleteLink: string;
  weekdayShort: readonly string[];
  monthShort: readonly string[];
};

const GHOST_STRINGS: Record<GhostBookingLocale, GhostDictionary> = {
  ru: {
    loading: "Загружаем профиль…",
    timezoneLabel: (tz: string) => `Часовой пояс: ${tz}`,
    chooseDay: "Выберите день",
    chooseTime: "Выберите время",
    yourName: "Как вас зовут (необязательно)",
    yourNamePlaceholder: "Имя",
    confirm: "Забронировать в Telegram",
    openingTelegram: "Открываем Telegram…",
    preparingMessage: "Готовим сообщение…",
    openingLink: "Открываем…",
    messageWithoutBooking: "Написать без брони",
    errorSubmit: "Не получилось создать заявку. Попробуйте ещё раз.",
    disclaimer: "Неофициальная страница, создана автоматически по публичному профилю Telegram.",
    claimLink: "Это моя страница — забрать",
    deleteLink: "Удалить страницу",
    weekdayShort: ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
    monthShort: [
      "янв",
      "фев",
      "мар",
      "апр",
      "мая",
      "июн",
      "июл",
      "авг",
      "сен",
      "окт",
      "ноя",
      "дек",
    ],
  },
  en: {
    loading: "Loading profile…",
    timezoneLabel: (tz: string) => `Timezone: ${tz}`,
    chooseDay: "Choose a day",
    chooseTime: "Choose a time",
    yourName: "Your name (optional)",
    yourNamePlaceholder: "Name",
    confirm: "Book via Telegram",
    openingTelegram: "Opening Telegram…",
    preparingMessage: "Preparing your message…",
    openingLink: "Opening…",
    messageWithoutBooking: "Just message",
    errorSubmit: "Couldn't create the request. Please try again.",
    disclaimer: "Unofficial page, generated automatically from the public Telegram profile.",
    claimLink: "This is my page — claim it",
    deleteLink: "Delete this page",
    weekdayShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    monthShort: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
  },
};

function ghostLocaleFromLanguage(language?: string | null): GhostBookingLocale {
  const raw = (language || (typeof navigator !== "undefined" ? navigator.language : "en") || "en")
    .split("-")[0]
    ?.toLowerCase();
  return raw === "ru" ? "ru" : "en";
}

function ghostVisitorTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function ghostDayLabel(day: GhostDay, strings: GhostDictionary): string {
  const [, month, date] = day.dateKey.split("-").map(Number);
  return `${strings.weekdayShort[day.weekday]}, ${date} ${strings.monthShort[month - 1]}`;
}

function ghostDeleteMailto(username: string): string {
  const subject = `Удалить гостевую страницу @${username}`;
  const body = `https://book.knopka.click/${username}`;
  return `mailto:help@knopka.click?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const pillClass = (active: boolean) =>
  [
    "shrink-0 rounded-lg border px-3 py-2 text-sm whitespace-nowrap transition-colors",
    active
      ? "border-inverted bg-inverted text-inverted"
      : "border-subtle bg-default text-default hover:border-emphasis",
  ].join(" ");

type Props = {
  /** Already validated against GHOST_USERNAME_PATTERN by the caller. */
  username: string;
  /** Rendered as-is when the profile turns out to be exists:false/claimed/error — the plain cal.diy 404. */
  fallback: ReactNode;
};

export function KnopkaGhostBookingView({ username, fallback }: Props) {
  const { i18n } = useLocale();
  const locale = ghostLocaleFromLanguage(i18n?.language);
  const strings = GHOST_STRINGS[locale];
  const timezone = useMemo(() => ghostVisitorTimezone(), []);
  const router = useRouter();

  const [state, setState] = useState<ViewState>("checking");
  const [profile, setProfile] = useState<GhostProfile | null>(null);
  const [days, setDays] = useState<GhostDay[]>([]);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [selectedHour, setSelectedHour] = useState<number | null>(null);
  const [visitorName, setVisitorName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Kept true after a successful POST while openTelegramDeepLink() takes over —
  // the confirm button must not flip back to its idle state before Telegram opens.
  const [redirecting, setRedirecting] = useState(false);
  // Which of the bottom/secondary actions is mid-transition, if any. Only one
  // at a time: clicking any of them is blocked while another is pending.
  const [pendingLink, setPendingLink] = useState<"claim" | "delete" | "message" | null>(null);
  const pendingLinkResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // WebApp.colorScheme, mirrored onto a `dark` class on our own markup — the
  // real cal.diy 404 (the `fallback` branch) manages its own theme already.
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  useEffect(() => {
    return () => {
      if (pendingLinkResetRef.current) clearTimeout(pendingLinkResetRef.current);
    };
  }, []);

  // Item 4: ready()+expand() once, only inside a real Mini App launch.
  useEffect(() => {
    prepareTelegramViewport();
  }, []);

  // Item 5: native Telegram back button instead of relying on the swipe
  // gesture only. Cleanup removes the click handler and hides the button
  // again — required, or the handler would outlive the page.
  useEffect(() => {
    if (!isTelegramMiniApp()) return;
    const handleBack = () => {
      try {
        if (typeof window !== "undefined" && window.history.length > 1) {
          window.history.back();
        } else {
          closeTelegramMiniApp();
        }
      } catch {
        // best-effort — never let this throw back into Telegram's callback
      }
    };
    return subscribeGhostTelegramBackButton(handleBack);
  }, []);

  // Item 6: mirror the client's theme onto our own markup, live.
  useEffect(() => {
    if (!isTelegramMiniApp()) return;
    setIsDarkTheme(ghostTelegramColorScheme() === "dark");
    return subscribeGhostTelegramTheme((scheme) => setIsDarkTheme(scheme === "dark"));
  }, []);

  // Item 7: prefill from initDataUnsafe.user, but only as a starting value —
  // never clobber something the visitor already typed. Runs once on mount,
  // well before that could happen in practice, and the functional update
  // still re-checks just in case.
  useEffect(() => {
    if (!isTelegramMiniApp()) return;
    const prefill = ghostTelegramVisitorName();
    if (!prefill) return;
    setVisitorName((current) => current || prefill);
  }, []);

  // Page title: setGhostDocumentTitle() is a no-op until a profile exists, so
  // this never touches the title on the "unavailable" (real cal.diy 404) branch.
  useEffect(() => {
    if (!profile) return;
    setGhostDocumentTitle(profile.displayName || `@${profile.username}`);
  }, [profile]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      const result = await fetchGhostTelegramProfile(username, controller.signal);
      if (cancelled) return;
      if (result.status !== "available") {
        setState("unavailable");
        return;
      }
      const schedule = buildGhostSchedule(timezone);
      if (!schedule.length) {
        // Nothing bookable in the whole window — no point showing an empty page.
        setState("unavailable");
        return;
      }
      setProfile(result.profile);
      setDays(schedule);
      setSelectedDateKey(schedule[0].dateKey);
      // Item 8: first available hour pre-selected so confirm is enabled from
      // the start — an explicit click on another hour still overrides this.
      setSelectedHour(schedule[0].slots[0]?.hour ?? null);
      setState("ready");
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [username, timezone]);

  // "при exists:false или ошибке API — показываем обычную 404 cal.diy без мигания":
  // the loading branch never renders the ghost card nor the 404 copy, so there's
  // nothing to flash before settling on one or the other.
  if (state === "checking") {
    return (
      <div
        className={[
          "bg-default flex min-h-screen items-center justify-center px-4",
          isDarkTheme ? "dark" : "",
        ].join(" ")}>
        <p className="text-subtle text-sm">{strings.loading}</p>
      </div>
    );
  }

  if (state === "unavailable" || !profile) {
    return <>{fallback}</>;
  }

  const selectedDay = days.find((day) => day.dateKey === selectedDateKey) ?? days[0];
  const selectedSlot = selectedDay?.slots.find((slot) => slot.hour === selectedHour) ?? null;
  const displayName = profile.displayName || `@${profile.username}`;

  const handleSelectDay = (day: GhostDay) => {
    setSelectedDateKey(day.dateKey);
    // Item 8: first hour of the newly selected day, so confirm stays enabled
    // without a second click — an explicit pick below still overrides this.
    setSelectedHour(day.slots[0]?.hour ?? null);
  };

  const handleSelectHour = (hour: number) => {
    ghostHaptic("select");
    setSelectedHour(hour);
  };

  const handleConfirm = async () => {
    if (!selectedSlot || submitting || redirecting || pendingLink) return;
    setSubmitting(true);
    const result = await submitGhostBookingRequest({
      username: profile.username,
      slotStartUtcIso: selectedSlot.startUtcIso,
      timezone,
      visitorName,
      locale,
    });
    if (!result.ok) {
      showToast(strings.errorSubmit, "error");
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    // Stays true past this point on purpose — see the `redirecting` state
    // declaration above. openTelegramDeepLink() either hands off to Telegram
    // or does a full page navigation; either way there is nothing to reset to.
    setRedirecting(true);
    ghostHaptic("success");
    openTelegramDeepLink(result.telegramUrl);
  };

  const scheduleLinkReset = () => {
    if (pendingLinkResetRef.current) clearTimeout(pendingLinkResetRef.current);
    pendingLinkResetRef.current = setTimeout(() => {
      setPendingLink(null);
      pendingLinkResetRef.current = null;
    }, GHOST_LINK_BUSY_RESET_MS);
  };

  const handleClaimClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // Same origin — stay inside the Mini App via next/navigation instead of a
    // full reload. href is kept on the <a> for accessibility/right-click.
    event.preventDefault();
    if (pendingLink || submitting || redirecting) return;
    setPendingLink("claim");
    router.push(`/signup?username=${encodeURIComponent(profile.username)}`);
    scheduleLinkReset();
  };

  const handleDeleteClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // mailto: isn't understood by WebApp.openLink(), so openExternalUrl()
    // falls back to a plain navigation for it — see knopkaGhostTelegram.ts.
    event.preventDefault();
    if (pendingLink || submitting || redirecting) return;
    setPendingLink("delete");
    openExternalUrl(ghostDeleteMailto(profile.username));
    scheduleLinkReset();
  };

  // Item 9: secondary path for visitors who don't want to pick a time at all.
  // No slot required, and no POST — just a plain t.me/<username> deep link.
  const handleMessageWithoutBooking = () => {
    if (pendingLink || submitting || redirecting) return;
    setPendingLink("message");
    openTelegramDeepLink(`https://t.me/${encodeURIComponent(profile.username)}`);
    scheduleLinkReset();
  };

  return (
    <div
      className={["bg-default min-h-screen px-4 pb-16 pt-10 sm:pt-16", isDarkTheme ? "dark" : ""].join(" ")}
      data-testid="knopka-ghost-booking-page">
      <main className="mx-auto w-full max-w-lg">
        <div className="border-subtle bg-default rounded-2xl border p-6 shadow-sm sm:p-8">
          <div className="flex items-center gap-4">
            <Avatar
              size="lg"
              imageSrc={profile.avatarUrl || ghostTelegramAvatarUrl(profile.username)}
              alt={displayName}
              title={displayName}
            />
            <div className="min-w-0">
              <h1 className="text-emphasis truncate text-lg font-semibold">{displayName}</h1>
              <p className="text-subtle text-sm">@{profile.username}</p>
            </div>
          </div>

          {profile.bio ? <p className="text-default mt-4 text-sm leading-relaxed">{profile.bio}</p> : null}

          <p className="text-subtle mt-4 text-xs">{strings.timezoneLabel(timezone)}</p>

          <div className="mt-6">
            <p className="text-emphasis text-sm font-medium">{strings.chooseDay}</p>
            <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
              {days.map((day) => (
                <button
                  key={day.dateKey}
                  type="button"
                  onClick={() => handleSelectDay(day)}
                  className={pillClass(day.dateKey === selectedDay?.dateKey)}>
                  {ghostDayLabel(day, strings)}
                </button>
              ))}
            </div>
          </div>

          {selectedDay ? (
            <div className="mt-4">
              <p className="text-emphasis text-sm font-medium">{strings.chooseTime}</p>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {selectedDay.slots.map((slot) => (
                  <button
                    key={slot.hour}
                    type="button"
                    onClick={() => handleSelectHour(slot.hour)}
                    className={pillClass(slot.hour === selectedHour)}>
                    {slot.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="mt-5">
            <TextField
              label={strings.yourName}
              name="ghost-visitor-name"
              placeholder={strings.yourNamePlaceholder}
              value={visitorName}
              maxLength={64}
              onChange={(event) => setVisitorName(event.target.value)}
            />
          </div>

          <Button
            type="button"
            className="mt-2 w-full justify-center"
            disabled={!selectedSlot || submitting || redirecting || Boolean(pendingLink)}
            // Deliberately not using the Button `loading` prop here: it hides
            // children behind `visibility:hidden` (see @calcom/ui Button.tsx),
            // which would make the "Opening Telegram…" label invisible right
            // when it matters most. `disabled` alone still blocks re-clicks
            // and applies the disabled visual style; our own spinner+label
            // below stays fully visible during the redirect.
            loading={submitting && !redirecting}
            aria-busy={submitting || redirecting}
            onClick={handleConfirm}>
            {redirecting ? (
              <span className="flex items-center justify-center gap-2">
                <Icon name="loader" className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
                <span>{strings.openingTelegram}</span>
              </span>
            ) : (
              strings.confirm
            )}
          </Button>
          {submitting || redirecting ? (
            <p className="text-emphasis mt-2 text-center text-xs font-medium" aria-live="polite">
              {redirecting ? strings.openingTelegram : strings.preparingMessage}
            </p>
          ) : null}

          <Button
            type="button"
            color="secondary"
            className="mt-2 w-full justify-center"
            disabled={Boolean(pendingLink) || submitting || redirecting}
            aria-busy={pendingLink === "message"}
            onClick={handleMessageWithoutBooking}>
            {pendingLink === "message" ? strings.openingLink : strings.messageWithoutBooking}
          </Button>

          <p className="text-muted mt-5 text-xs leading-relaxed">{strings.disclaimer}</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <a
              href={`/signup?username=${encodeURIComponent(profile.username)}`}
              onClick={handleClaimClick}
              aria-busy={pendingLink === "claim"}
              className={[
                "text-emphasis underline underline-offset-2",
                pendingLink ? "pointer-events-none" : "",
              ].join(" ")}>
              {pendingLink === "claim" ? strings.openingLink : strings.claimLink}
            </a>
            <a
              href={ghostDeleteMailto(profile.username)}
              onClick={handleDeleteClick}
              aria-busy={pendingLink === "delete"}
              className={[
                "text-subtle underline underline-offset-2",
                pendingLink ? "pointer-events-none" : "",
              ].join(" ")}>
              {pendingLink === "delete" ? strings.openingLink : strings.deleteLink}
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
